import { c as _c } from "react/compiler-runtime";
import type { I18n } from '@funkey/locales';
import { useFormState, type FormState } from '~/modules/form';
import { CheckoutAccountFormFields } from './account/CheckoutAccountPageForm';
import { CheckoutInfoFieldset } from './info/CheckoutInfoFieldset';
import { CheckoutCateringFields } from './extra/CheckoutCateringFieldset';
import Title from '~/components/utils/Title';
import { FormInputErrors, Textarea } from '~/components/primitives';
import { Auth } from '~/modules/auth/client';
import { useEffect, useMemo } from 'react';
import { useLocalStorageState } from '~/modules/browser/storage';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { maybeMap, std } from '@funkey/shared';
import { z } from 'zod';
export namespace QuoteRequestFormFields {
  export type FormValues = CheckoutAccountFormFields.FormValues & CheckoutInfoFieldset.FormValues & CheckoutCateringFields.FormValues & {
    remarks: string;
    voucherCode: string | null;
  };
  export const Component: React.FC<{
    i18n: I18n;
    formState: FormState<FormValues> | FormState.Subform<FormValues>;
  }> = props => {
    const $ = _c(41);
    let t0;
    if ($[0] !== props.formState) {
      t0 = props.formState.$subform();
      $[0] = props.formState;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== t0) {
      t1 = <CheckoutAccountFormFields.FieldSet formState={t0} />;
      $[2] = t0;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <hr className="my-10 rounded-md text-blue-200" />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== props.formState) {
      t3 = props.formState.$subform();
      $[5] = props.formState;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== props.i18n || $[8] !== t3) {
      t4 = <CheckoutInfoFieldset.Component formState={t3} i18n={props.i18n} requiresActivitySelection={true} />;
      $[7] = props.i18n;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== props.formState) {
      t5 = props.formState.$subform();
      $[10] = props.formState;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== props.formState.values.participants || $[13] !== props.i18n || $[14] !== t5) {
      t6 = <CheckoutCateringFields.Component formState={t5} i18n={props.i18n} participants={props.formState.values.participants} />;
      $[12] = props.formState.values.participants;
      $[13] = props.i18n;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== props.i18n) {
      t7 = props.i18n._("Other questions/comments");
      $[16] = props.i18n;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== t7) {
      t8 = <Title as="legend" size="xl" weight="bold" className="mb-8">{t7}</Title>;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const t9 = props.formState.values.remarks;
    let t10;
    if ($[20] !== props.formState) {
      t10 = e => props.formState.handleChange("remarks", e.currentTarget.value);
      $[20] = props.formState;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== props.i18n) {
      t11 = props.i18n._("contact.form.message.placeholder");
      $[22] = props.i18n;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== props.formState.values.remarks || $[25] !== t10 || $[26] !== t11) {
      t12 = <Textarea className="mt-1" id="remarks" value={t9} rows={5} onChange={t10} placeholder={t11} />;
      $[24] = props.formState.values.remarks;
      $[25] = t10;
      $[26] = t11;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== props.formState) {
      t13 = props.formState.getErrorMessages("remarks");
      $[28] = props.formState;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t13) {
      t14 = <FormInputErrors errors={t13} />;
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t12 || $[33] !== t14 || $[34] !== t8) {
      t15 = <fieldset className="col-span-2">{t8}{t12}{t14}</fieldset>;
      $[32] = t12;
      $[33] = t14;
      $[34] = t8;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== t1 || $[37] !== t15 || $[38] !== t4 || $[39] !== t6) {
      t16 = <div className="w-full space-y-10">{t1}{t2}{t4}{t6}{t15}</div>;
      $[36] = t1;
      $[37] = t15;
      $[38] = t4;
      $[39] = t6;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    return t16;
  };
  export function useValuesCache() {
    const $ = _c(11);
    const auth = Auth.useSession();
    let t0;
    if ($[0] !== auth.user) {
      t0 = CheckoutAccountFormFields.defaultValues(auth.user);
      $[0] = auth.user;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = CheckoutInfoFieldset.defaultValues();
      t2 = CheckoutCateringFields.defaultValues();
      $[2] = t1;
      $[3] = t2;
    } else {
      t1 = $[2];
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t0) {
      t3 = {
        ...t0,
        ...t1,
        ...t2,
        remarks: "",
        voucherCode: null
      };
      $[4] = t0;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const storage = useLocalStorageState("QUOTE_REQUEST_FORM", t3, "sessionStorage");
    const t4 = storage.state;
    let t5;
    if ($[6] !== storage.store) {
      t5 = storage.store.setValue.bind(storage.store);
      $[6] = storage.store;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== storage.state || $[9] !== t5) {
      t6 = {
        value: t4,
        sync: t5
      };
      $[8] = storage.state;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  export function useState(i18n) {
    const $ = _c(20);
    const cachedFormState = QuoteRequestFormFields.useValuesCache();
    const t0 = cachedFormState.value;
    let t1;
    if ($[0] !== i18n) {
      t1 = CheckoutAccountFormFields.FORM_VALIDATION(i18n);
      $[0] = i18n;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const t2 = CheckoutInfoFieldset.useValidation(i18n);
    let t3;
    if ($[2] !== i18n) {
      t3 = i18n._("checkout.info.participants.min");
      $[2] = i18n;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t3) {
      t4 = {
        validate: _temp,
        message: t3
      };
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== i18n) {
      t5 = i18n._("This field is required");
      $[6] = i18n;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== t5) {
      t6 = {
        validate: _temp2,
        message: t5
      };
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6) {
      t7 = {
        ...t1,
        ...t2,
        participants: t4,
        remarks: t6
      };
      $[10] = t1;
      $[11] = t2;
      $[12] = t4;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const formState = useFormState(t0, t7 satisfies FormState.Validation<FormValues>);
    let t8;
    if ($[15] !== cachedFormState || $[16] !== formState.values) {
      t8 = () => {
        cachedFormState.sync(formState.values);
      };
      $[15] = cachedFormState;
      $[16] = formState.values;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== formState.values) {
      t9 = [formState.values];
      $[18] = formState.values;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    useEffect(t8, t9);
    return formState;
  }
  function _temp2(value_0) {
    return z.string().min(1).safeParse(value_0).success;
  }
  function _temp(value) {
    return typeof value === "number" && value >= 15;
  }
  export function formValuesToCreateDealInput(formValues: QuoteRequestFormFields.FormValues) {
    return {
      // [User Info]
      contact: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        phone: formValues.phone,
        optinSubscribeToNewsletter: formValues.optinSubscribeToNewsletter ?? false
      },
      customer: {
        portal: 'PUBLIC',
        vatNumber: formValues.vatNumber,
        businessName: formValues.businessName
      },
      // [Deal Info]
      startsAt: CheckoutInfoFieldset.parseStartsAt(formValues.startsAt, formValues.startTime),
      endsAt: maybeMap(formValues.endsAt, x => new Date(x)),
      participants: formValues.participants!,
      location: formValues.address ? {
        address: formValues.address,
        userProvided: formValues.userProvidesLocation,
        radius: formValues.radius
      } : null,
      budget: !!formValues.budget && !!formValues.budgetType ? {
        euro: formValues.budget,
        type: formValues.budgetType as any
      } : null,
      remarks: formValues.remarks,
      dateTimeIndication: formValues.dateTimeIndication,
      durationType: formValues.durationType,
      // [Upselling]
      catering: {
        meta: std.Object.mapValues(formValues.catering, catering => std.Object.omit(catering, ['ids'])),
        ids: Object.values(formValues.catering).map(x => x.ids).flat()
      },
      serviceIDs: [],
      // [Meta]
      voucherCode: formValues.voucherCode
    } satisfies Omit<TRPC_ROUTER.Inputs['deal']['create'], 'items' | 'locale' | 'source'>;
  }
}